<template>
  <div class="d-flex flex-column h-100">
    <div class="manipulation-area py-2 pl-3 pr-0">
      <b-row>
        <b-col class="ai-texter-search">
          <CommonFilter v-model="viewModel.searchTerm" :placeholder="$t('AiTexter.FilterByAttributeName')" prepend-icon="Filter" trigger-by-time></CommonFilter>
        </b-col>
      </b-row>
    </div>
    <div class="ai-texter-table mt-2" v-if="sortedList.length > 0">
      <div class="ai-texter-table-header">
        <div class="ai-texter-table-col options-column pr-0">
          <b-checkbox v-model="viewModel.selectAll" @change="toggleSelectAll" :indeterminate="isIndeterminate" />
        </div>
        <div class="ai-texter-table-col name-col pl-0" :class="{ 'sorted-by': viewModel.sortBy.name == 'name' }" @click="setSortBy('name')">
          <div class="pl-2">{{ $t("AiTexter.Attribute") }}</div>
          <div class="ml-2" v-if="viewModel.sortBy.name == 'name'">
            <Icon icon="ChevronBottom" class="font-weight-bolder" v-if="!viewModel.sortBy.desc" />
            <Icon icon="ChevronTop" class="font-weight-bolder" v-if="viewModel.sortBy.desc" />
          </div>
        </div>

        <div class="ai-texter-table-col mid-col pl-0" :class="{ 'sorted-by': viewModel.sortBy.name == 'value' }" @click="setSortBy('value')">
          <div class="pl-2">{{ $t("AiTexter.Value") }}</div>
          <div class="ml-2" v-if="viewModel.sortBy.name == 'value'">
            <Icon icon="ChevronBottom" class="font-weight-bolder" v-if="!viewModel.sortBy.desc" />
            <Icon icon="ChevronTop" class="font-weight-bolder" v-if="viewModel.sortBy.desc" />
          </div>
        </div>

        <div class="ai-texter-table-col small-col pl-0" :class="{ 'sorted-by': viewModel.sortBy.name == 'userType' }" @click="setSortBy('userType')">
          <div class="pl-2">{{ $t("AiTexter.AttributeType") }}</div>
          <div class="ml-2" v-if="viewModel.sortBy.name == 'userType'">
            <Icon icon="ChevronBottom" class="font-weight-bolder" v-if="!viewModel.sortBy.desc" />
            <Icon icon="ChevronTop" class="font-weight-bolder" v-if="viewModel.sortBy.desc" />
          </div>
        </div>

        <div class="ai-texter-table-col small-col pl-0" :class="{ 'sorted-by': viewModel.sortBy.name == 'isAsn' }" @click="setSortBy('isAsn')">
          <div class="pl-2">{{ $t("AiTexter.ASNActivated") }}</div>
          <div class="ml-2" v-if="viewModel.sortBy.name == 'isAsn'">
            <Icon icon="ChevronBottom" class="font-weight-bolder" v-if="!viewModel.sortBy.desc" />
            <Icon icon="ChevronTop" class="font-weight-bolder" v-if="viewModel.sortBy.desc" />
          </div>
        </div>
      </div>
      <div class="ai-texter-table-overflow-container">
        <div v-for="(sortedItem, index) in sortedList" :key="sortedItem.attributeId" class="ai-texter-table-row">
          <div class="ai-texter-table-row-container">
            <div class="ai-texter-table-row-data" :data-cy="'ai-texter-table-row-' + index">
              <div class="ai-texter-table-col options-column pr-0">
                <b-checkbox v-model="sortedItem.selected" @change="(e) => handleCheckboxChange(e, sortedItem)" />
              </div>
              <div class="ai-texter-table-col name-col pl-0" @click="sortedItem.selected = !sortedItem.selected" v-tooltip="sortedItem.name">
                <span class="pl-2">{{ sortedItem.name }}</span>
              </div>
              <div
                v-if="sortedItem.value && sortedItem.value.trim() !== ''"
                class="ai-texter-table-col mid-col pl-0"
                @click="sortedItem.selected = !sortedItem.selected"
                v-tooltip="`${sortedItem.value} ${sortedItem.unit}`">
                <span class="pl-2" :data-cy="'ai-texter-attribute-data-name-' + index"
                  >{{ sortedItem.value }} <span v-if="sortedItem.unit">{{ sortedItem.unit }}</span>
                </span>
              </div>
              <div class="ai-texter-table-col small-col pl-0" @click="sortedItem.selected = !sortedItem.selected" v-tooltip="sortedItem.userType">
                <span class="pl-2">{{ sortedItem.userType == "" ? `<${$t("AiTexter.attributeTypeEmpty")}>` : sortedItem.userType }}</span>
              </div>
              <div class="ai-texter-table-col small-col pl-0" @click="sortedItem.selected = !sortedItem.selected">
                <span class="pl-2">{{ _.has(sortedItem, "isAsn") ? $t("Yes") : $t("No") }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <Alert with-icon show variant="info">
        {{ $t("AiTexter.NoAttributeAvailable") }}
      </Alert>
    </div>
  </div>
</template>

<script setup lang="ts">
import Alert from "@common/components/Alert.vue";
import CommonFilter from "@common/components/CommonFilter.vue";
import Icon from "@common/components/Icon.vue";
import { SortedTypes } from "@medapro-types/ChannelCreator/components/AiTexter.d.ts";
import _ from "lodash";
import { computed, onMounted, reactive, ref } from "vue";

//#region General
const props = defineProps<{ items: any; isText?: boolean; isAiTexterOpen?: boolean }>();
const emits = defineEmits(["update:selectedItems"]);
//#endregion

type ViewModel = {
  attributes: any;
  selectedAttributes: any;
  sortBy: { name: SortedTypes; desc: boolean };
  selectAll: any;
  searchTerm: string;
  inputIsFocused: boolean;
};

const viewModel = reactive<ViewModel>({
  attributes: {},
  selectedAttributes: [],
  sortBy: { name: "", desc: false },
  selectAll: ref(false),
  searchTerm: "",
  inputIsFocused: false
});

const sortedList = computed(() => {
  const filter = viewModel.searchTerm ? viewModel.searchTerm.toLowerCase() : null;
  return _.chain(props.items)
    .filter((x: { name: any; value: string }) => x.name && x.value && x.value.trim() !== "" && (!filter || _.includes(x.name.toLowerCase(), filter)))
    .orderBy([viewModel.sortBy.name], [viewModel.sortBy.desc ? "desc" : "asc"])
    .value();
});

const setSortBy = (sortByName: SortedTypes) => {
  if (viewModel.sortBy.name != sortByName) {
    viewModel.sortBy = { name: sortByName, desc: false };
  } else {
    viewModel.sortBy.desc = !viewModel.sortBy.desc;
  }
};

const isIndeterminate = computed(() => {
  const selectedCount = sortedList.value.filter((item: any) => item.selected).length;
  return selectedCount > 0 && selectedCount < sortedList.value.length;
});

const toggleSelectAll = () => {
  const isSelected = viewModel.selectAll;
  props.items.forEach((item: any) => (item.selected = isSelected));
  viewModel.selectedAttributes = viewModel.selectAll ? props.items?.filter((item) => item.value !== "") : [];
  emits("update:selectedItems", viewModel.selectedAttributes);
};

function handleCheckboxChange(e: any, parameter: any) {
  if (e) {
    viewModel.selectedAttributes.push(parameter);
  } else {
    viewModel.selectedAttributes = viewModel.selectedAttributes.filter((item: any) => item.attributeId !== parameter.attributeId);
  }
  const selectedCount = sortedList.value.filter((item: any) => item.selected).length;
  viewModel.selectAll = selectedCount === sortedList.value.length;

  emits("update:selectedItems", viewModel.selectedAttributes);
}
onMounted(() => {
  if (props.isText && sortedList.value.length > 0) {
    viewModel.selectAll = true;
    toggleSelectAll();
  }
});
</script>
